import styled from 'styled-components';
import { useState } from 'react';
import { useDebouncedState } from '@react-hookz/web';

import { Location } from '../../../../features/location/Location';

import { suggestedSearches } from '../../../../components/PageWrappers/SharedComponents/GlobalSearchModal/suggestedSearches';
import { BaseSearch } from '../../../../components/Toolkit/SearchModal/BaseSearch';
import { InputButton } from '../../../../components/Toolkit/InputButton/InputButton';
import { SearchModal } from '../../../../components/Toolkit/SearchModal/SearchModal';
import { useAutoCompleteValues } from '../../Layouts/queries/useAutoCompleteValues';

import { useLocationWriteState } from '../../../../features/location/hooks/LocationWriteState/LocationWriteState.hook';

import type { IHandleSelectSearchItem } from '../../../../components/Toolkit/SearchModal/BaseSearch';
import type { TLastSearch } from '../../../../components/PageWrappers/SharedComponents/LastSearchCard/types';

import { Desktop } from '../../../../components/Support/Desktop/Desktop';
import { Mobile } from '../../../../components/Support/Mobile/Mobile';

import type { DisplayContainerConfig } from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { PAGE } from '../../../../helpers/pages';

export interface VerticalSearchProps {
  baseUrl: string;
  query: string;
  onInput: (t: string) => void;
  onSearch: (t: string) => void;
  onHandleSelectSearchItem: ({
    autoSuggestedText,
    searchRequest,
    url,
    category,
  }: IHandleSelectSearchItem) => void;
  lastSearchData?: TLastSearch;
  lastSearchDataTrackingValue?: string;
  searchPlaceholder: string;
}

const Container = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 48px auto;
  gap: ${({ theme }) => theme.spacing.M16};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

function VerticalSearch({
  baseUrl,
  query,
  onInput,
  onSearch,
  onHandleSelectSearchItem,
  lastSearchData,
  lastSearchDataTrackingValue,
  searchPlaceholder,
}: VerticalSearchProps) {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const [searchQueryDebounced, setSearchQueryDebounced] = useDebouncedState(
    query,
    200,
  );
  const autoCompleteResponse = useAutoCompleteValues(
    searchQueryDebounced,
    PAGE.HOME,
  );

  const { handleSelectListItem: onSelect } = useLocationWriteState();

  const bottomSheetConfig: DisplayContainerConfig = {
    small: 'block',
    medium: 'none',
    large: 'none',
  };
  const dropdownConfig: DisplayContainerConfig = {
    small: 'none',
    medium: 'block',
    large: 'block',
  };

  const handleSearchInputChange = (newText: string) => {
    onInput(newText);
    setSearchQueryDebounced(newText);
  };

  return (
    <Container>
      <Mobile>
        <InputButton
          isActive={isSearchModalOpen}
          value={query}
          onClick={() => setIsSearchModalOpen(true)}
          placeholder={searchPlaceholder}
          variant="SEARCH"
        />
      </Mobile>
      <Desktop>
        <BaseSearch
          variant="INLINE"
          onInput={handleSearchInputChange}
          onHandleSearch={onSearch}
          onHandleSelectSearchItem={onHandleSelectSearchItem}
          initialSearchValues={suggestedSearches(baseUrl)}
          placeholder={searchPlaceholder}
          autoSuggestItems={autoCompleteResponse.data?.suggestions ?? []}
          lastSearchData={lastSearchData}
          lastSearchDataTrackingValue={lastSearchDataTrackingValue}
        />
      </Desktop>
      <Location
        skeletonListLength={16}
        maxHeight="376px"
        secondarySelectWidth="116px"
        maxTextContainerWidthMWeb="120px"
        onSelect={onSelect}
        bottomSheetConfig={bottomSheetConfig}
        dropdownConfig={dropdownConfig}
        stackVertically
        verticalGap="M16"
        heightVariant="LARGE"
      />
      {isSearchModalOpen && (
        <SearchModal
          askToClose={() => setIsSearchModalOpen(false)}
          isOpen={isSearchModalOpen}
          placeholder={searchPlaceholder}
          presetInput={query}
          onInput={handleSearchInputChange}
          onSearch={onSearch}
          onHandleSelectSearchItem={onHandleSelectSearchItem}
          initialSearchValues={suggestedSearches(baseUrl)}
          autoSuggestItems={autoCompleteResponse.data?.suggestions ?? []}
          lastSearchData={lastSearchData}
        />
      )}
    </Container>
  );
}

export { VerticalSearch };
